import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, Check, MessageCircle, Calendar, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { businessLd, contactPageLd, breadcrumbLd } from "../components/StructuredData";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
  };

  return (
    <>
      <Seo
        title="Contact Ananda Paudel — Interior Designer & Furniture Specialist, Nepal"
        description="Reach Ananda Paudel directly. WhatsApp, phone, or email. Based in Kathmandu, serving all of Nepal. Typical reply within a few hours."
        path="/contact"
        jsonLd={[
          businessLd(),
          contactPageLd(),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="bg-cream-100 border-b border-cream-200 py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-2">
              Talk to Ananda
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light">
              Get in Touch
            </h1>
            <p className="font-sans text-sm sm:text-base text-matte-500 mt-3 max-w-2xl leading-relaxed">
              Quick question, project enquiry, or just exploring? Send a
              short message and Ananda will personally reply.
            </p>
            <p className="font-sans text-xs text-matte-400 mt-3">
              Looking for a structured project brief?{" "}
              <Link to="/book" className="text-walnut-500 hover:underline inline-flex items-center gap-1">
                Book a free consultation instead <ArrowRight size={12} />
              </Link>
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <h2 className="font-serif text-3xl text-matte-900 font-light mb-4">
                How Ananda works with you
              </h2>
              <p className="font-sans text-sm text-matte-500 leading-relaxed mb-8">
                There's no showroom to visit — and that's by design. Ananda
                works directly with you: understanding your taste, your
                space, and your budget, then sourcing or building exactly
                what you need through his 40-year network of Nepal's finest
                factories and craftsmen.
              </p>

              <div className="space-y-6 mb-10">
                {[
                  {
                    icon: <MessageCircle size={18} />,
                    label: "WhatsApp (Preferred)",
                    value: "Chat directly on WhatsApp",
                    href: "https://wa.me/9779841488271",
                  },
                  {
                    icon: <Phone size={18} />,
                    label: "Call",
                    value: "+977-9841488271",
                    href: "tel:+9779841488271",
                  },
                  {
                    icon: <Mail size={18} />,
                    label: "Email",
                    value: "ananda.paudel.work@gmail.com",
                    href: "mailto:ananda.paudel.work@gmail.com",
                  },
                  {
                    icon: <Clock size={18} />,
                    label: "Response time",
                    value: "Typically within a few hours (Sun–Fri, 9–7)",
                  },
                ].map((item) => (
                  <div key={item.label} className="flex gap-4">
                    <div className="w-10 h-10 bg-walnut-100 text-walnut-500 rounded-full flex items-center justify-center flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="font-sans text-xs tracking-wider text-matte-400 uppercase mb-0.5">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="font-sans text-sm text-matte-700 hover:text-walnut-600 transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-sans text-sm text-matte-700">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-matte-900 rounded-sm p-6 sm:p-8">
                <h3 className="font-serif text-xl text-white mb-6">What happens next</h3>
                <div className="space-y-5">
                  {[
                    {
                      step: "1",
                      title: "You reach out",
                      desc:
                        "Send a short message — photos, sketches, or just words are all fine.",
                    },
                    {
                      step: "2",
                      title: "Ananda replies",
                      desc:
                        "Personally — by your preferred channel. Usually within a few hours.",
                    },
                    {
                      step: "3",
                      title: "Free 30-min consultation",
                      desc:
                        "We map your space, taste, and budget. No pressure, no fee.",
                    },
                    {
                      step: "4",
                      title: "Design and build (if you want)",
                      desc:
                        "Tailored design, factory-direct fabrication, delivery and install.",
                    },
                  ].map((s) => (
                    <div key={s.step} className="flex gap-4">
                      <div className="w-7 h-7 rounded-full bg-walnut-600 text-cream-100 font-sans text-xs flex items-center justify-center flex-shrink-0 font-semibold">
                        {s.step}
                      </div>
                      <div>
                        <p className="font-sans text-sm font-medium text-cream-200 mb-0.5">{s.title}</p>
                        <p className="font-sans text-xs text-matte-400 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to="/book"
                  className="mt-7 inline-flex items-center gap-2 bg-walnut-500 hover:bg-walnut-600 text-white font-sans text-sm tracking-wider px-6 py-3 rounded-sm transition-colors"
                >
                  <Calendar size={14} />
                  Book a Free Consultation
                </Link>
              </div>
            </div>

            <div>
              {sent ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-16 h-16 bg-walnut-100 text-walnut-500 rounded-full flex items-center justify-center mb-6">
                    <Check size={28} />
                  </div>
                  <h2 className="font-serif text-3xl text-matte-900 mb-3">Message sent</h2>
                  <p className="font-sans text-sm text-matte-500 max-w-xs">
                    Thanks for reaching out. Ananda will personally reply within
                    a few hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-2">
                    Send Ananda a Message
                  </p>
                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Name *
                      </label>
                      <input
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-white border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                        Phone / WhatsApp
                      </label>
                      <input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full bg-white border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                        placeholder="+977-"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                      Email *
                    </label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full bg-white border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                      What is this about?
                    </label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full bg-white border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm"
                    >
                      <option value="">Select a topic</option>
                      <option>General question</option>
                      <option>Quick project enquiry</option>
                      <option>Existing project</option>
                      <option>Trade / collaboration</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-sans text-xs tracking-wider text-matte-400 uppercase block mb-2">
                      Message *
                    </label>
                    <textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full bg-white border border-cream-300 px-4 py-3 font-sans text-sm text-matte-900 outline-none focus:border-walnut-400 transition-colors rounded-sm resize-none"
                      placeholder="A few lines is enough — Ananda will follow up by your preferred channel."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-matte-900 text-cream-100 font-sans text-sm tracking-wider py-4 hover:bg-walnut-700 transition-colors"
                  >
                    Send Message
                  </button>
                  <p className="font-sans text-xs text-matte-400 text-center">
                    Prefer to call?{" "}
                    <a href="tel:+9779841488271" className="text-walnut-500 hover:underline">
                      +977-9841488271
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
